import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import * as Dialog from '@radix-ui/react-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Send,
  Files,
  History,
  Shield,
  BarChart3,
  FileSearch,
  Ban,
  ScrollText,
  User,
  LogIn,
  LogOut,
  HelpCircle,
  Menu,
  Download,
  Settings,
  CloudCog,
  MailWarning,
  UserCheck,
  Home,
  Building2,
  X,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useQuery } from '@tanstack/react-query';
import { isNavigationActive, primaryNavigation, signInPath } from '@/lib/navigation-usability';

const userNavIcons = {
  '/': Home,
  '/send': Send,
  '/receive': Download,
  '/files': Files,
  '/activity': History,
  '/approvals': UserCheck,
};

const userNavItems = primaryNavigation.map(item => ({ ...item, icon: userNavIcons[item.to] }));
const externalNavItems = userNavItems.filter(item => item.to !== '/receive' && item.to !== '/approvals');

const adminNavItems = [
  { to: '/admin', icon: BarChart3, label: 'Dashboard' },
  { to: '/admin/files', icon: FileSearch, label: 'File Search' },
  { to: '/admin/blocklist', icon: Ban, label: 'Blocklist' },
  { to: '/admin/audit', icon: ScrollText, label: 'Audit Log' },
  { to: '/admin/users', icon: Shield, label: 'Users' },
  { to: '/admin/identity-providers', icon: Building2, label: 'Identity Providers' },
  { to: '/admin/sync', icon: CloudCog, label: 'Sync' },
  { to: '/admin/failed-emails', icon: MailWarning, label: 'Failed Emails' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export function Layout() {
  const { user, isAdmin, isLoading, isExternal } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { data: approvalData } = useQuery({
    queryKey: ['pendingApprovals', user?.id],
    queryFn: () => api.get<{ success: boolean; data: { count: number } }>('/api/access-requests/pending/count'),
    enabled: !!user && !isExternal,
    staleTime: 30000,
  });
  const pendingApprovals = approvalData?.data.count ?? 0;
  const mainRef = useRef<HTMLElement>(null);

  const isAdminRoute = location.pathname.startsWith('/admin');

  useEffect(() => {
    const desktop = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => {
      if (desktop.matches) setSidebarOpen(false);
    };
    desktop.addEventListener('change', closeOnDesktop);
    return () => desktop.removeEventListener('change', closeOnDesktop);
  }, []);

  // Choose nav items based on user type
  const getNavItems = () => {
    if (isAdmin && isAdminRoute) return adminNavItems;
    if (isExternal) return externalNavItems;
    return userNavItems;
  };

  const navItems = getNavItems();
  const showSidebar = !!user;

  const navigation = (
    <nav aria-label={isAdmin && isAdminRoute ? 'Administration' : 'Main navigation'} className="p-4 space-y-1">
      {navItems.map(item => {
        const isActive = isNavigationActive(item.to, location.pathname);
        const badgeCount = item.to === '/approvals' ? pendingApprovals : 0;
        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={isActive ? 'page' : undefined}
            onClick={() => setSidebarOpen(false)}
            className={cn(
              'flex min-w-0 items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isActive ? 'bg-primary text-primary-foreground' : 'text-gray-700 hover:bg-gray-100'
            )}
          >
            <item.icon aria-hidden="true" className="h-5 w-5 shrink-0" />
            <span className="break-words">{item.label}</span>
            {badgeCount > 0 && (
              <Badge variant="destructive" className="ml-auto text-xs px-1.5 py-0.5 min-w-5 text-center">
                <span className="sr-only">Pending: </span>{badgeCount}
              </Badge>
            )}
          </Link>
        );
      })}
    </nav>
  );

  return (
    <Dialog.Root open={sidebarOpen && showSidebar} onOpenChange={setSidebarOpen}>
    <div className="min-h-screen bg-gray-50">
      <a
        href="#main-content"
        onClick={() => mainRef.current?.focus()}
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-[60] focus:rounded-md focus:bg-white focus:px-4 focus:py-2 focus:text-primary focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>
      {/* Header */}
      <header className="bg-battelle-blue text-white shadow-md sticky top-0 z-40">
        <div className="container mx-auto px-4">
          <div className="flex h-16 min-w-0 items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2 sm:gap-4">
              {user && (
                <Dialog.Trigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden shrink-0 text-white hover:bg-white/10 hover:text-white"
                  aria-label="Open navigation"
                  title="Open navigation"
                >
                  <Menu aria-hidden="true" className="h-5 w-5" />
                </Button>
                </Dialog.Trigger>
              )}
              <Link to="/" aria-label="Battelle File Exchange home" className="flex min-w-0 items-center gap-2 rounded-sm focus-visible:ring-2 focus-visible:ring-white">
                <Shield aria-hidden="true" className="h-8 w-8 shrink-0" />
                <span className="font-semibold text-lg hidden sm:inline truncate">
                  Battelle File Exchange
                </span>
              </Link>
            </div>

            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              {/* Mode Toggle */}
              {isAdmin && (
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/10 hover:text-white"
                  >
                    <Link to={isAdminRoute ? '/' : '/admin'}>{isAdminRoute ? 'User Mode' : 'Admin'}</Link>
                  </Button>
              )}

              {/* Help */}
                <Button
                  asChild
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to="/help" aria-label="Help" title="Help"><HelpCircle aria-hidden="true" className="h-5 w-5" /></Link>
                </Button>

              {/* User Menu */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/10 hover:text-white gap-2"
                      aria-label={`Account menu for ${user.displayName || user.email}`}
                      title="Account menu"
                    >
                      <User aria-hidden="true" className="h-4 w-4 shrink-0" />
                      <span className="hidden sm:inline max-w-32 lg:max-w-48 truncate">
                        {user.displayName || user.email}
                      </span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56 max-w-[calc(100vw-2rem)]">
                    <DropdownMenuLabel>
                      <div className="flex flex-col break-words">
                        <span>{user.displayName || 'User'}</span>
                        <span className="font-normal text-xs text-muted-foreground">
                          {user.email}
                        </span>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-red-600"
                      onClick={async () => {
                        if (isExternal) {
                          // External users: clear session cookie via API
                          try {
                            await fetch('/public/auth/logout', { method: 'POST', credentials: 'include', headers: { 'X-FX-CSRF': '1' } });
                          } catch { /* ignore */ }
                          localStorage.removeItem('userEmail');
                          window.location.href = '/';
                        } else {
                          window.location.href = '/api/auth/logout';
                        }
                      }}
                    >
                      <LogOut className="mr-2 h-4 w-4" />
                      Sign out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                  <Button
                    asChild
                    variant="ghost"
                    size="sm"
                    className="text-white hover:bg-white/10 hover:text-white"
                  >
                    <Link
                      to={signInPath(location.pathname, location.search, location.hash)}
                      aria-disabled={isLoading}
                      onClick={event => { if (isLoading) event.preventDefault(); }}
                    >
                      <LogIn aria-hidden="true" className="h-4 w-4 mr-2" />
                      Sign in
                    </Link>
                  </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="flex min-w-0">
        {/* Sidebar - only shown for authenticated users */}
        {showSidebar && (
          <aside className="hidden lg:block w-64 shrink-0 bg-white border-r">
            {navigation}
          </aside>
        )}

        {/* Main Content */}
        <main id="main-content" ref={mainRef} tabIndex={-1} className="min-w-0 flex-1 p-4 lg:p-8 min-h-[calc(100vh-4rem)] focus:outline-none">
          <Outlet />
        </main>
      </div>

      {/* Footer */}
      <footer className="border-t bg-white py-4">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} Battelle Memorial Institute. All rights reserved.</p>
        </div>
      </footer>
    </div>
    {showSidebar && (
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 h-dvh w-64 max-w-[calc(100vw-2rem)] overflow-y-auto border-r bg-white shadow-lg"
          onCloseAutoFocus={event => {
            if (window.matchMedia('(min-width: 1024px)').matches) {
              event.preventDefault();
              mainRef.current?.focus();
            }
          }}
        >
          <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
            <Dialog.Title className="text-lg font-semibold">Navigation</Dialog.Title>
            <Dialog.Close asChild>
              <Button variant="ghost" size="icon" aria-label="Close navigation" title="Close navigation">
                <X aria-hidden="true" className="h-5 w-5" />
              </Button>
            </Dialog.Close>
          </div>
          {navigation}
        </Dialog.Content>
      </Dialog.Portal>
    )}
    </Dialog.Root>
  );
}
